import { useEffect } from "react";
import { FormattedMessage } from "react-intl";
import { StyleSheet } from "react-native";
import { z } from "zod";
import { Button } from "~/components/Button";
import { Icon } from "~/components/Icon";
import { Screen } from "~/components/Screen";
import { Spacer } from "~/components/Spacer";
import { Text } from "~/components/Text";
import { styleConstants } from "~/style/styleConstants";
import { logEvent } from "../analytics/firebaseAnalytics";
import type { AxiosError } from "axios";

const styles = StyleSheet.create({
  errorScreen: {
    paddingTop: 87,
    paddingHorizontal: styleConstants.spacingExtraLarge,
    justifyContent: "flex-start",
    alignItems: "center",
    backgroundColor: styleConstants.colorBackgroundWhite,
  },
  tryAgainButton: {
    width: 250,
    height: 50,
    borderRadius: 30,
  },
});

export function SmsTosConsentErrorView({
  onRetry,
  error,
}: {
  onRetry: () => void;
  error: Error;
}) {
  useEffect(() => {
    logEvent("terms_error");
  }, []);

  const errorMessage = getErrorMessage(error);

  return (
    <Screen
      withBottomInset
      withTopInset={false}
      preventScroll
      style={styles.errorScreen}
    >
      <Spacer extraExtraLarge />
      <Icon name="close" size={66} color={styleConstants.colorTextDanger} />
      <Spacer extraLarge />
      <Text alignCenter weightBold sizeExtraExtraLarge>
        <FormattedMessage
          id="pnF/9H"
          defaultMessage="Something went wrong"
          description="Title on error screen for SMS TOS consent saying that something went wrong."
        />
      </Text>
      <Spacer extraLarge />
      <Text alignCenter weightLight>
        <FormattedMessage
          id="EMGu+t"
          defaultMessage="Could not accept the terms. Please try again or come back later."
          description="Text on error screen for SMS TOS consent saying that the terms and conditions could not be accepted."
        />
      </Text>
      <Spacer />
      {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
      <Text colorDanger sizeSmall>{`Error: ${errorMessage}`}</Text>

      <Spacer extraLarge />
      <Spacer extraLarge />

      <Button
        style={styles.tryAgainButton}
        outline
        colorTheme
        onPress={() => {
          onRetry();
        }}
        title={intl.formatMessage({
          id: "CariJR",
          defaultMessage: "Retry",
          description:
            "Button text on error screen for SMS TOS consent saying to try again.",
        })}
      />
    </Screen>
  );
}

const errorSchema = z.object({
  message: z.string().optional(),
});

const getErrorMessage = (error: Error) => {
  if ("isAxiosError" in error) {
    const axiosError = error as AxiosError;
    const response = axiosError.response;
    const errorMessage = response?.data;

    const parsedError = errorSchema.safeParse(errorMessage);

    if (parsedError.success && parsedError.data.message) {
      return parsedError.data.message;
    }

    return error.message;
  }

  return error.message;
};
